/**
 * Placing a tile from an intent rather than from coordinates.
 *
 * The Assistant never gives grid positions. It says what it wants - "half
 * width", "beside the fuel chart", "on a new row" - and this module turns that
 * into a layout, the same way a person's drop does: through
 * `resolveCollisions`, so whatever is in the way falls straight down and
 * nothing ever overlaps, then `compactUp` when the dashboard floats its tiles.
 *
 * Pure, like `layout.ts`, so every placement rule is a unit test rather than a
 * screenshot.
 */

import { GRID_COLS, MIN_TILE_W, findSlot, overlaps, type DashboardTile, type TilePos } from './dashboard-state.ts';
import { compactUp, gridRows, resolveCollisions } from './layout.ts';

export type TileSize = 'small' | 'third' | 'half' | 'wide' | 'full';
export type TileHeight = 'short' | 'normal' | 'tall';

/** Columns per named width, on the 12-column grid. */
export const SIZE_COLUMNS: Readonly<Record<TileSize, number>> = {
  small: 3,
  third: 4,
  half: 6,
  wide: 8,
  full: 12,
};

/** Rows per named height. `normal` is the height a chart is added at by hand. */
export const HEIGHT_ROWS: Readonly<Record<TileHeight, number>> = {
  short: 6,
  normal: 9,
  tall: 14,
};

export interface PlacementIntent {
  /** Same row, directly right of this tile - or left when there is no room right. */
  beside?: string | null;
  /** Directly under this tile. */
  below?: string | null;
  /** A row of its own under everything. */
  newRow?: boolean;
}

export interface PlacementResult {
  /** Every tile's position afterwards, the placed one included. */
  positions: Array<{ id: string; pos: TilePos }>;
  /** Set when the intent could not be honoured exactly, in words for the chat. */
  note?: string;
}

type Sized = { w: number; h: number };

/** Whether a rect is free of every tile but the ignored one. */
function free(tiles: DashboardTile[], rect: TilePos, ignore?: string): boolean {
  return rect.x >= 0 && rect.x + rect.w <= GRID_COLS && !tiles.some((t) => t.id !== ignore && overlaps(t.pos, rect));
}

/**
 * Where a `w`×`h` tile starts for this intent, before anything is pushed aside.
 *
 * "Beside" checks only the anchor's top row for room: a taller neighbour lower
 * down is pushed under the new tile rather than making "beside" impossible.
 * When the full width does not fit but a narrower tile would, the tile is
 * narrowed to the room there is, down to the minimum width - a dashboard that
 * put the chart somewhere else entirely would be a worse surprise than one a
 * little narrower than asked.
 */
function startFor(
  others: DashboardTile[],
  size: Sized,
  intent: PlacementIntent,
  anchorName: (id: string) => string,
): { pos: TilePos; note?: string } {
  const { w, h } = size;
  const beside = intent.beside ? others.find((t) => t.id === intent.beside) : undefined;
  const below = intent.below ? others.find((t) => t.id === intent.below) : undefined;

  if (intent.beside && !beside) {
    return { pos: { ...findSlot(others, w, h), w, h }, note: `could not find ${intent.beside} to sit beside` };
  }
  if (intent.below && !below) {
    return { pos: { ...findSlot(others, w, h), w, h }, note: `could not find ${intent.below} to sit below` };
  }

  if (beside) {
    const y = beside.pos.y;
    const rightX = beside.pos.x + beside.pos.w;
    const row = (x: number, width: number): TilePos => ({ x, y, w: width, h: 1 });
    if (free(others, row(rightX, w))) return { pos: { x: rightX, y, w, h } };
    const leftX = beside.pos.x - w;
    if (leftX >= 0 && free(others, row(leftX, w))) return { pos: { x: leftX, y, w, h } };

    // Narrow into whatever room the row has, right first.
    let room = 0;
    while (rightX + room < GRID_COLS && free(others, row(rightX + room, 1))) room++;
    if (room >= MIN_TILE_W) {
      return {
        pos: { x: rightX, y, w: room, h },
        note: `narrowed to fit beside ${anchorName(beside.id)}`,
      };
    }
    room = 0;
    while (beside.pos.x - room - 1 >= 0 && free(others, row(beside.pos.x - room - 1, 1))) room++;
    if (room >= MIN_TILE_W) {
      return {
        pos: { x: beside.pos.x - room, y, w: room, h },
        note: `narrowed to fit beside ${anchorName(beside.id)}`,
      };
    }
    return {
      pos: { x: Math.min(beside.pos.x, GRID_COLS - w), y: beside.pos.y + beside.pos.h, w, h },
      note: `no room beside ${anchorName(beside.id)}, so placed below it`,
    };
  }

  if (below) {
    return {
      pos: { x: Math.max(0, Math.min(below.pos.x, GRID_COLS - w)), y: below.pos.y + below.pos.h, w, h },
    };
  }

  if (intent.newRow) return { pos: { x: 0, y: gridRows(others), w, h } };

  return { pos: { ...findSlot(others, w, h), w, h } };
}

/**
 * Put tile `id` into `others` by intent.
 *
 * `others` must not contain the tile itself. The placed tile keeps the start
 * position it was given and everything it lands on falls straight down - the
 * rule a drop follows - and the whole tab floats up afterwards when `float` is
 * on, which is also what closes a gap the tile left behind when it moved.
 */
export function placeTile(
  others: DashboardTile[],
  id: string,
  size: Sized,
  intent: PlacementIntent,
  float: boolean,
  anchorName: (id: string) => string = (x) => x,
): PlacementResult {
  const width = Math.max(MIN_TILE_W, Math.min(GRID_COLS, size.w));
  const { pos, note } = startFor(others, { w: width, h: size.h }, intent, anchorName);
  const subject = { id, pos } as DashboardTile;
  const resolved = resolveCollisions([...others, subject], id);
  const positions = float ? compactUp(resolved) : resolved;
  return note ? { positions, note } : { positions };
}

/**
 * Change a tile's size where it stands.
 *
 * Grows to the left when it would pass the right edge, so "make it full width"
 * works on a tile sitting on the right. Neighbours it now covers fall down.
 */
export function resizeInPlace(
  tiles: DashboardTile[],
  id: string,
  size: Sized,
  float: boolean,
): Array<{ id: string; pos: TilePos }> {
  const tile = tiles.find((t) => t.id === id);
  if (!tile) return tiles.map((t) => ({ id: t.id, pos: t.pos }));
  const w = Math.max(MIN_TILE_W, Math.min(GRID_COLS, size.w));
  const x = Math.min(tile.pos.x, GRID_COLS - w);
  const from = tile.pos;
  const next = tiles.map((t) => (t.id === id ? { ...t, pos: { x, y: from.y, w, h: size.h } } : t));
  const resolved = resolveCollisions(next, id);
  return float ? compactUp(resolved) : resolved;
}

/**
 * Re-flow a tab into `columns` equal columns, in reading order.
 *
 * Every tile takes the column width; each row is as tall as its tallest tile,
 * and the shorter tiles in it are stretched to match, because a row of ragged
 * bottoms is exactly the untidiness someone asking to arrange a tab means.
 */
export function arrangeColumns(
  tiles: DashboardTile[],
  columns: number,
): Array<{ id: string; pos: TilePos }> {
  const cols = Math.max(1, Math.min(4, Math.round(columns)));
  const w = Math.floor(GRID_COLS / cols);
  const ordered = [...tiles].sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x);
  const out: Array<{ id: string; pos: TilePos }> = [];
  let y = 0;
  for (let i = 0; i < ordered.length; i += cols) {
    const row = ordered.slice(i, i + cols);
    const h = Math.max(...row.map((t) => t.pos.h));
    row.forEach((t, j) => out.push({ id: t.id, pos: { x: j * w, y, w, h } }));
    y += h;
  }
  return out;
}

export interface LayoutRowIntent {
  /** Tile ids, left to right. */
  ids: string[];
  /** One named width per tile; omitted splits the row equally. */
  sizes?: TileSize[] | null;
  /** One height for the whole row; omitted keeps the tallest tile's. */
  height?: TileHeight | null;
}

/**
 * Lay a tab out as explicit rows, top to bottom.
 *
 * What the Assistant asks for when it means a shape rather than one move - "KPIs
 * across the top, the trend full width, two scatters side by side". Every row
 * is as tall as asked (or its tallest tile), so its bottoms line up. Unnamed
 * widths split the row equally, and the last tile takes any remainder, so an
 * equal row always fills the width. Tiles the rows do not name keep their size
 * and follow underneath in reading order, so nothing is lost by leaving it out.
 */
export function layoutRows(
  tiles: DashboardTile[],
  rows: readonly LayoutRowIntent[],
): Array<{ id: string; pos: TilePos }> {
  const byId = new Map(tiles.map((t) => [t.id, t]));
  const out: Array<{ id: string; pos: TilePos }> = [];
  const placed = new Set<string>();
  let y = 0;
  for (const row of rows) {
    const members = row.ids.filter((id) => byId.has(id) && !placed.has(id));
    if (!members.length) continue;
    const named = row.sizes && row.sizes.length === row.ids.length ? row.sizes : null;
    const equal = Math.floor(GRID_COLS / members.length);
    const widths = members.map((id, i) => {
      if (named) return SIZE_COLUMNS[named[row.ids.indexOf(id)]!];
      return i === members.length - 1 ? GRID_COLS - equal * (members.length - 1) : equal;
    });
    const h = row.height ? HEIGHT_ROWS[row.height] : Math.max(...members.map((id) => byId.get(id)!.pos.h));
    let x = 0;
    members.forEach((id, i) => {
      const w = Math.max(MIN_TILE_W, Math.min(GRID_COLS - x, widths[i]!));
      out.push({ id, pos: { x, y, w, h } });
      placed.add(id);
      x += w;
    });
    y += h;
  }
  // The rest, in their own reading order and at their own sizes, below.
  const rest = tiles
    .filter((t) => !placed.has(t.id))
    .sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x);
  if (rest.length) {
    const top = Math.min(...rest.map((t) => t.pos.y));
    for (const t of rest) out.push({ id: t.id, pos: { ...t.pos, y: y + (t.pos.y - top) } });
  }
  return out;
}
